import { useBooleanState } from "@gettactic/helpers";
import { cn } from "@gettactic/helpers/src/css/cn";
import { mergeRefs } from "@gettactic/helpers/src/dom/mergeRef";
import React from "react";
import { FormControlWrapper } from "../FormControlWrapper";
import { FormControlOutline } from "../FormControlWrapper/FormControlOutline";
import { FormControlSlot } from "../FormControlWrapper/FormControlSlot";
import {
  useFormControlInputA11y,
  useFormControlWrapper
} from "../FormControlWrapper/FormControlWrapperProvider";
import {
  disabledCssClasses,
  disabledOutlineClasses
} from "../FormControlWrapper/defines";
import { splitFormControlWrapperProps } from "../FormControlWrapper/helpers";
import {
  TextInputProps,
  TextInputPropsWithFormControl
} from "./TextInput.types";
import { Button } from "@cmp-src/primitives/button";
import { Eye, EyeOff } from "lucide-react";

export const TextInputInput = React.forwardRef<
  HTMLInputElement,
  TextInputProps
>((props, ref) => {
  const { inputClassName, className, type, disableOutline, ...rest } = props;
  const { id, setCharactersCounter, minLength, maxLength, isReadOnly } =
    useFormControlWrapper();
  const a11yProps = useFormControlInputA11y({
    "aria-invalid": rest["aria-invalid"] === true || undefined,
    "aria-required": rest["aria-required"] === true || undefined,
    "aria-describedby": rest["aria-describedby"]
  });
  const inputRef = React.useRef<HTMLInputElement>(null);
  const isPasswordVisible = useBooleanState();

  React.useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const updateCounter = () => {
      setCharactersCounter?.(input.value.length || 0);
    };
    const handleInput = (event: Event) => {
      if (event.target === input) updateCounter();
    };

    // Count the initial value (e.g. a react-hook-form default) and every edit after it.
    // Listen on the document, not the input: a listener on the input runs before React's
    // onChange, the browser flushes React work in between, and re-rendering a controlled
    // input there, still with its old value, drops the keystroke
    updateCounter();
    const ownerDocument = input.ownerDocument;
    ownerDocument.addEventListener("input", handleInput);

    return () => {
      ownerDocument.removeEventListener("input", handleInput);
    };
  }, [inputRef.current]);

  return (
    <>
      <input
        {...rest}
        className={cn(
          "flex h-8 w-full border-0 bg-transparent text-sm shadow-none outline-none ring-0 transition-colors placeholder:text-muted-foreground",
          disabledCssClasses,
          disabledOutlineClasses,
          inputClassName,
          className
        )}
        style={{
          borderWidth: 0,
          outline: 0,
          border: 0,
          boxShadow: "none",
          ...rest.style
        }}
        maxLength={maxLength}
        minLength={minLength}
        ref={mergeRefs([ref, inputRef])}
        type={
          type === "password"
            ? isPasswordVisible.state
              ? "text"
              : "password"
            : type
        }
        id={id}
        {...a11yProps}
        readOnly={isReadOnly || rest.readOnly}
        tabIndex={isReadOnly ? -1 : rest.tabIndex}
      />
      {type === "password" && (
        <FormControlSlot>
          <Button
            type={"button"}
            size={"sm"}
            variant={"ghost"}
            aria-label={
              isPasswordVisible.state ? "Hide password" : "Show password"
            }
            aria-pressed={isPasswordVisible.state}
            iconLeft={isPasswordVisible.state ? EyeOff : Eye}
            onClick={() => isPasswordVisible?.toggle()}
          />
        </FormControlSlot>
      )}
    </>
  );
});

TextInputInput.displayName = "TextInputInput";

/**
 * A single-line text field with label, messages and validation states (`FormControlWrapper`).
 * `type="password"` adds a show / hide toggle. Inside react-hook-form use `HookFormTextInput`.
 */
export const TextInput = React.forwardRef<
  HTMLInputElement,
  TextInputPropsWithFormControl
>((props, ref) => {
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(props);
  const isDisabled = wrapperProps.isDisabled || inputProps.disabled;
  const isReadOnly = wrapperProps.isReadOnly || inputProps.readOnly;

  return (
    <FormControlWrapper
      enableSlotsInputFocus
      {...wrapperProps}
      wrapperClassName={cn("shadow-input", wrapperProps.wrapperClassName)}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
    >
      <TextInputInput ref={ref} {...inputProps} disabled={isDisabled} />
      {inputProps.disableOutline !== true && <FormControlOutline />}
    </FormControlWrapper>
  );
});

TextInput.displayName = "TextInput";
